import  { useState } from "react";
import { useSelector } from "react-redux";
import Layout from "../components/layout/Layout";
import { 
  Card,
  CardContent,
  CardHeader,
  Table, 
  TableHead, 
  TableBody, 
  TableRow, 
  TableCell, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Alert, 
  Button, 
  TextField,
  Checkbox, 
  Radio,
  RadioGroup,
  FormControlLabel,
  Select,
  MenuItem,
  Box
} from "@mui/material";
import {
  ShieldOutlined,
  Send,
  CheckCircle
} from "@mui/icons-material";
import IncomeTax from "../components/CAPortal/IncomeTax";
import TrustandCompany from "../components/CAPortal/TrustandCompany";

const CaPortal = () => {
  const [formState, setFormState] = useState({
    subscribed: false,
    dueDate: "",
    compliance: "",
    comments: "",
    approved: false,
    audited: false,
    clientComments: "",
    signedOff: false
  });

  const [isVerified, setIsVerified] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [showDialog, setShowDialog] = useState(false);

  const selectedCompany = useSelector(state => state.kyc.selectedCompanyInfo);

  const handleFormUpdate = (field, value) => {
    setFormState(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const verifyData = () => {
    if (!formState.dueDate || !formState.compliance) {
      setShowAlert(true);
      return;
    }
    setIsVerified(true);
    setShowAlert(true);
  };

  const handleSubmit = async () => {
    setShowDialog(false);
    setIsSubmitting(true);

    try {
      await fetch('/api/ca-portal/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          companyId: selectedCompany?.id,
          formData: formState,
          verificationDate: new Date().toISOString()
        })
      });

      setShowAlert(true);
      setFormState({
        subscribed: false,
        dueDate: "",
        compliance: "",
        comments: "",
        approved: false,
        audited: false,
        clientComments: "",
        signedOff: false
      });
      setIsVerified(false);
    } catch (error) {
      console.error("Submission error:", error);
      setShowAlert(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Layout>
      <Box sx={{ padding: '24px' }}>
        <Card>
          <CardHeader
            title={
              <Box>
                <Box component="h1" sx={{ fontSize: '1.5rem', fontWeight: 'bold', m: 0 }}>
                  CA Portal
                </Box>
                <Box component="p" sx={{ fontSize: '0.875rem', opacity: 0.9, m: 0 }}>
                  {selectedCompany?.legalName}
                </Box>
              </Box>
            }
            action={
              <Box sx={{ display: "flex", gap: "16px" }}>
              <Button
                variant={isVerified ? "contained" : "outlined"}
                onClick={verifyData}
                disabled={isVerified}
                startIcon={<ShieldOutlined />}
                sx={{
                  borderColor: "white",
                  color: "white",
                  "&:hover": {
                    borderColor: "white",
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                  },
                }}
              >
                {isVerified ? "Verified" : "Verify Data"}
              </Button>

              <Button
                variant="contained"
                onClick={() => setShowDialog(true)}
                disabled={!isVerified || isSubmitting}
                startIcon={<Send />}
                sx={{
                  border: "1px solid white",
                  color: "white",
                  backgroundColor: "red",
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.2)",
                  },
                }}
              >
                {isSubmitting ? "Submitting..." : "Submit"}
              </Button>
            </Box>
            }
            sx={{ 
              background: '#f01e2c', 
              color: 'white',
              '& .MuiCardHeader-action': {
                margin: 0,
                alignSelf: 'center'
              }
            }}
          />

          <CardContent>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell>Particulars</TableCell>
                  <TableCell>Subscribed</TableCell>
                  <TableCell>Due Date</TableCell>
                  <TableCell>Compliance</TableCell>
                  <TableCell>Comments</TableCell>
                  <TableCell>Approved</TableCell>
                  <TableCell>Audited</TableCell>
                  <TableCell>Client Comments</TableCell>
                  <TableCell>Signed Off</TableCell>
                </TableRow>

                <TableRow sx={{ bgcolor: '#fff3e0' }}>
                  <TableCell>Input Row</TableCell>
                  <TableCell>
                    <Checkbox 
                      checked={formState.subscribed}
                      onChange={(e) => handleFormUpdate('subscribed', e.target.checked)}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField 
                      type="date"
                      value={formState.dueDate}
                      onChange={(e) => handleFormUpdate('dueDate', e.target.value)}
                      size="small"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                  </TableCell>
                  <TableCell>
                    <Select
                      value={formState.compliance}
                      onChange={(e) => handleFormUpdate('compliance', e.target.value)}
                      size="small"
                      fullWidth
                    >
                      <MenuItem value="Done">Done</MenuItem>
                      <MenuItem value="Pending">Pending</MenuItem>
                      <MenuItem value="Processing">Processing</MenuItem>
                      <MenuItem value="Not Initiated">Not Initiated</MenuItem>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <TextField
                      placeholder="Add comment"
                      value={formState.comments}
                      onChange={(e) => handleFormUpdate('comments', e.target.value)}
                      size="small"
                      fullWidth
                    />
                  </TableCell>
                  <TableCell>
                    <RadioGroup
                      value={formState.approved.toString()}
                      onChange={(e) => handleFormUpdate('approved', e.target.value === 'true')}
                    >
                      <FormControlLabel value="true" control={<Radio />} label="" />
                    </RadioGroup>
                  </TableCell>
                  <TableCell>
                    <Checkbox
                      checked={formState.audited}
                      onChange={(e) => handleFormUpdate('audited', e.target.checked)}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      placeholder="Client comment"
                      value={formState.clientComments}
                      onChange={(e) => handleFormUpdate('clientComments', e.target.value)}
                      size="small"
                      fullWidth
                    />
                  </TableCell>
                  <TableCell>
                    <Checkbox
                      checked={formState.signedOff}
                      onChange={(e) => handleFormUpdate('signedOff', e.target.checked)}
                    />
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
              <TableRow className="bg-dark">
         
          </TableRow>
                <TableRow>
                  <TableCell 
                    colSpan={9} 
                    sx={{ 
                      bgcolor: '#f01e2c', 
                      color: 'white', 
                      fontWeight: 'bold' 
                    }}
                  >
                    1) ADVISING ON DRAFTING OF DEEDS/AGREEMENTS
                  </TableCell>
                </TableRow>
                
                <TableRow>
                  <TableCell sx={{ paddingLeft: '24px' }}>i) Partnership Deed</TableCell>
                  <TableCell></TableCell>
                  <TableCell></TableCell>
                  <TableCell></TableCell>
                  <TableCell></TableCell>
                  <TableCell></TableCell>
                  <TableCell></TableCell>
                  <TableCell></TableCell>
                  <TableCell></TableCell>
                </TableRow>
                <TableRow>
            <TableCell  className="p-0 ps-3">
              ii) Partnership Deed (With Consultation & Tax Advisory)
            </TableCell>
            <TableCell className="p-0"></TableCell>
            <TableCell className="p-0"></TableCell>
            <TableCell className="p-0"></TableCell>
            <TableCell className="p-0"></TableCell>
            <TableCell className="p-0"></TableCell>
            <TableCell className="p-0"></TableCell>
            <TableCell className="p-0"></TableCell>
            <TableCell className="p-0"></TableCell>
          </TableRow>
          <TableRow>
            <TableCell className="p-0 ps-3">
              Filling of Forms with Registrar of Firms
            </TableCell>
            <TableCell className="p-0"></TableCell>
            <TableCell className="p-0"></TableCell>
            <TableCell className="p-0"></TableCell>
            <TableCell className="p-0"></TableCell>
            <TableCell className="p-0"></TableCell>
            <TableCell className="p-0"></TableCell>
            <TableCell className="p-0"></TableCell>
            <TableCell className="p-0"></TableCell>
          </TableRow>
          <TableRow>
            <TableCell className="p-0 ps-3">
              Supplementary / Modification in Partnership Deed
            </TableCell>
            <TableCell className="p-0"></TableCell>
            <TableCell className="p-0"></TableCell>
            <TableCell className="p-0"></TableCell>
            <TableCell className="p-0"></TableCell>
            <TableCell className="p-0"></TableCell>
            <TableCell className="p-0"></TableCell>
            <TableCell className="p-0"></TableCell>
            <TableCell className="p-0"></TableCell>
          </TableRow>
          <TableRow>
            <TableCell className="p-0 ps-3">
              Joint Development Agreements / Joint Venture Agreements
            </TableCell>
            <TableCell className="p-0"></TableCell>
            <TableCell className="p-0"></TableCell>
            <TableCell className="p-0"></TableCell>
            <TableCell className="p-0"></TableCell>
            <TableCell className="p-0"></TableCell>
            <TableCell className="p-0"></TableCell>
            <TableCell className="p-0"></TableCell>
            <TableCell className="p-0"></TableCell>
          </TableRow>
          <TableRow>
            <TableCell className="p-0 ps-3">
              Others Deeds such as Power of Attorney, Will , Gift Deed etc.
            </TableCell>
            <TableCell className="p-0"></TableCell>
            <TableCell className="p-0"></TableCell>
            <TableCell className="p-0"></TableCell>
            <TableCell className="p-0"></TableCell>
            <TableCell className="p-0"></TableCell>
            <TableCell className="p-0"></TableCell>
            <TableCell className="p-0"></TableCell>
            <TableCell className="p-0"></TableCell>
          </TableRow>
          <IncomeTax />
          
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {showAlert && (
          <Alert 
            severity="success" 
            icon={<CheckCircle />}
            sx={{ marginTop: '16px' }}
          >
            {isVerified ? "Data verified successfully!" : "Data submitted successfully!"}
          </Alert>
        )}

        <Dialog 
          open={showDialog} 
          onClose={() => setShowDialog(false)}
        >
          <DialogTitle>Confirm Submission</DialogTitle>
          <DialogContent>
            Are you sure you want to submit this data? This action cannot be undone.
          </DialogContent>
          <DialogActions>
            <Button 
              variant="outlined" 
              onClick={() => setShowDialog(false)}
            >
              Cancel
            </Button>
            <Button 
              variant="contained" 
              onClick={handleSubmit} 
              disabled={isSubmitting}
            >
              {isSubmitting ? "Submitting..." : "Submit"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
      <TrustandCompany />
    </Layout>
  );
};

export default CaPortal;